import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../css/admin/admin-mc-connections.css';
import './UserManagement.css';
import logo from '../../images/audithippo.png';

const USERS_API_URL        = '/rest/api/v1/admin/users';
const INVITE_API_URL       = '/rest/api/v1/admin/users/invite';
const USER_UPDATE_API_URL  = '/rest/api/v1/admin/users/update';
const USER_REMOVE_API_URL  = '/rest/api/v1/admin/users/remove';
const CONNECTIONS_API_URL  = '/rest/api/v1/admin/connections';

const ROLE_OPTIONS = ['Admin', 'User'];

const EMPTY_INVITE = { name: '', email: '', role: 'User', connectionId: '' };

function buildInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function normalizeInstance(inst) {
    if (!inst) return null;
    if (typeof inst === 'string') return { id: inst, name: inst, status: 'Active', isActive: true };
    return {
        id:       inst.id || inst.connectionId || inst.name || '',
        name:     inst.name || inst.instanceName || '',
        status:   inst.status || (inst.isActive ? 'Active' : 'Inactive'),
        isActive: typeof inst.isActive === 'boolean' ? inst.isActive : (inst.status || '').toLowerCase() === 'active',
    };
}

function normalizeUser(u, idx) {
    const rawInstances = Array.isArray(u.instances) ? u.instances : [];
    return {
        id:         u.id || idx + 1,
        initials:   u.initials || buildInitials(u.name),
        name:       u.name || '',
        email:      u.email || '',
        role:       u.role || 'Viewer',
        status:     u.status || 'Active',
        instances:  rawInstances.map(normalizeInstance).filter(Boolean),
        lastActive: u.lastActive || '—',
    };
}

const FILTERS = ['All', 'Admin', 'User', 'Pending'];

function UserManagement() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState('All');
    const [users, setUsers] = useState([]);
    const [inviteOpen, setInviteOpen]             = useState(false);
    const [inviteForm, setInviteForm]             = useState(EMPTY_INVITE);
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [inviteError, setInviteError]           = useState(null);
    const [inviteSuccess, setInviteSuccess]       = useState(null);
    const [showInviteValidation, setShowInviteValidation] = useState(false);
    const [editOpen, setEditOpen]                 = useState(false);
    const [editForm, setEditForm]                 = useState({ userId: '', name: '', email: '', role: 'User', status: 'Active', connectionId: '' });
    const [editSubmitting, setEditSubmitting]     = useState(false);
    const [editError, setEditError]               = useState(null);
    const [editNote, setEditNote]                 = useState(null);
    const [connections, setConnections]           = useState([]);
    const [removingId, setRemovingId]             = useState(null);

    const fetchUsers = () => axios.get(USERS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.users || data.data || []);
            console.log('[UserManagement] users response:', data, '→ list length', list.length);
            setUsers(list.map(normalizeUser));
        })
        .catch(err => {
            console.error('[UserManagement] failed to load users:', err.response?.status, err.response?.data, err.message);
            setUsers([]);
        });

    const fetchConnections = () => axios.get(CONNECTIONS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.connections || data.data || []);
            setConnections(list);
        })
        .catch(err => {
            console.error('Failed to load MC connections:', err);
            setConnections([]);
        });

    useEffect(() => {
        fetchUsers();
        fetchConnections();
    }, []);

    const openInvite = () => {
        setInviteForm(EMPTY_INVITE);
        setInviteError(null);
        setInviteSuccess(null);
        setShowInviteValidation(false);
        setInviteOpen(true);
    };
    const closeInvite = () => {
        if (inviteSubmitting) return;
        setInviteOpen(false);
        setInviteForm(EMPTY_INVITE);
        setInviteError(null);
        setInviteSuccess(null);
        setShowInviteValidation(false);
    };
    const updateInvite = (field, value) => setInviteForm(prev => ({ ...prev, [field]: value }));

    const submitInvite = () => {
        if (inviteSubmitting) return;
        if (!inviteValid) { setShowInviteValidation(true); return; }
        setShowInviteValidation(false);
        setInviteSubmitting(true);
        setInviteError(null);
        setInviteSuccess(null);
        const payload = {
            name:         inviteForm.name,
            email:        inviteForm.email,
            role:         inviteForm.role,
            connectionId: inviteForm.connectionId,
        };
        console.log('[UserManagement] invite payload:', payload);
        axios.post(INVITE_API_URL, payload)
            .then(res => {
                console.log('[UserManagement] invite response:', res.status, res.data);
                const data = res.data || {};
                const sentTo = data.email || inviteForm.email;
                setInviteSuccess(`Invitation sent to ${sentTo}.`);
                setInviteForm(EMPTY_INVITE);
                fetchUsers();
            })
            .catch(err => {
                const status = err.response?.status;
                const data   = err.response?.data;
                console.error('[UserManagement] invite failed:', status, data, err.message, err);
                const detail = typeof data === 'string'
                    ? data
                    : (data?.message || data?.error || JSON.stringify(data || {}));
                let msg = `${status ? `[${status}] ` : ''}${detail || err.message}`;
                if (data && (data.paidSlots != null || data.usedSlots != null || data.remainingSlots != null)) {
                    msg += ` — Paid: ${data.paidSlots ?? '—'}, Used: ${data.usedSlots ?? '—'}, Remaining: ${data.remainingSlots ?? '—'}`;
                }
                setInviteError(msg);
            })
            .finally(() => setInviteSubmitting(false));
    };

    const inviteValid = inviteForm.name.trim() !== '' && inviteForm.email.trim() !== '' && inviteForm.connectionId !== '';

    const openEdit = (user) => {
        const role = ROLE_OPTIONS.includes(user.role) ? user.role : 'User';
        const firstInstance = Array.isArray(user.instances) && user.instances.length > 0 ? user.instances[0] : null;
        let connectionId = '';
        if (firstInstance) {
            connectionId = firstInstance.id
                || connections.find(c => (c.name || c.instanceName) === firstInstance.name)?.id
                || connections.find(c => (c.name || c.instanceName) === firstInstance.name)?.connectionId
                || '';
        }
        setEditForm({
            userId: user.id,
            name: user.name,
            email: user.email,
            role,
            status: user.status || 'Active',
            connectionId,
        });
        setEditError(null);
        setEditNote(null);
        setEditOpen(true);
    };
    const closeEdit = () => {
        if (editSubmitting) return;
        setEditOpen(false);
        setEditError(null);
        setEditNote(null);
    };
    const updateEdit = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

    const submitEdit = () => {
        if (editSubmitting) return;
        setEditSubmitting(true);
        setEditError(null);
        setEditNote(null);
        axios.post(USER_UPDATE_API_URL, {
            userId:       editForm.userId,
            name:         editForm.name,
            role:         editForm.role,
            status:       editForm.status,
            connectionId: editForm.connectionId,
        })
            .then(res => {
                const data = res.data || {};
                const note = data.connectionNote || data.note || data.connection?.note || null;
                fetchUsers();
                if (note) {
                    setEditNote(note);
                } else {
                    setEditOpen(false);
                }
            })
            .catch(err => setEditError(err.response?.data?.message || err.message))
            .finally(() => setEditSubmitting(false));
    };

    const editValid = editForm.name.trim() !== '' && editForm.email.trim() !== '';

    const handleRemove = (user) => {
        if (removingId) return;
        const ok = window.confirm(`Remove ${user.name || user.email}? This cannot be undone.`);
        if (!ok) return;
        setRemovingId(user.id);
        axios.post(USER_REMOVE_API_URL, { userId: user.id })
            .then(() => fetchUsers())
            .catch(err => {
                console.error('[UserManagement] failed to remove user:', err.response?.status, err.response?.data, err.message);
                window.alert(`Failed to remove user: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => setRemovingId(null));
    };

    const totalUsers   = users.length;
    const activeCount  = users.filter(u => u.status === 'Active').length;
    const adminCount   = users.filter(u => u.role === 'Admin').length;
    const userCount    = users.filter(u => u.role === 'User').length;
    const pendingCount = users.filter(u => u.status === 'Pending').length;

    const KPIS = [
        { icon: '\u{1F464}', label: 'Total Users',     value: String(totalUsers),   sub: `${activeCount} active`,        color: 'purple' },
        { icon: '\u{1F464}', label: 'Admins',          value: String(adminCount),   sub: 'full access',                  color: 'violet' },
        { icon: '\u{1F464}', label: 'Users',           value: String(userCount),    sub: 'standard access',              color: 'green' },
        { icon: '\u{1F464}', label: 'Pending Invites', value: String(pendingCount), sub: 'awaiting acceptance',          color: 'amber' },
    ];

    const filtered = users.filter(u => {
        if (activeFilter === 'All') return true;
        if (activeFilter === 'Pending') return u.status === 'Pending';
        return u.role === activeFilter;
    });

    return (
        <>
            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`} onClick={() => setMenuOpen(false)} />

            {/* Mobile Slide Menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                        <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                    </a>
                    <div className="mobile-menu-close" onClick={() => setMenuOpen(false)} role="button" aria-label="Close menu">&#10005;</div>
                </div>
                <div className="mobile-menu-items">
                    <div className="mobile-menu-item" onClick={() => navigate('/journeys')}>Journeys</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/data-extensions')}>Data Extensions</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/automations')}>Automations</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/email-studio')}>Email Studio</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/cloud-pages')}>Cloud Pages</div>
                    <div className="mobile-menu-item active">Admin</div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar-custom">
                <div className="adm-navbar-container">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                                <span /><span /><span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                                    <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Admin</span>
                            </div>
                        </div>
                        <button
                            className="environment-badge d-flex align-items-center gap-2"
                            onClick={() => { localStorage.removeItem('isAdminLoggedIn'); localStorage.removeItem('adminAuthToken'); localStorage.removeItem('adminSessionId'); navigate('/admin/login'); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            <div className="environment-dot" />
                            <span className="environment-text">Logout</span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Page */}
            <div className="adm-page">
                <div className="adm-admin-layout">
                    {/* Sidebar */}
                    <aside className="adm-sidebar">
                        <div className="adm-sidebar-box">
                            <div className="adm-sidebar-header">
                                <p className="adm-sidebar-title">Admin Settings</p>
                            </div>
                            <div className="adm-sidebar-tabs">
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/mc-connections')}>
                                    <span>&#9729;</span> MC Connections
                                </button>
                                <button className="adm-sidebar-tab active" type="button">
                                    <span>&#128101;</span> User Management
                                </button>
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/billing')}>
                                    <span>&#128179;</span> Billing &amp; Plan
                                </button>
                            </div>
                            <div className="adm-sidebar-back">
                                <button className="adm-btn-back" type="button" onClick={() => navigate('/automations')}>
                                    &larr; Back to AuditHippo
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="adm-main">
                        <div className="adm-section-header">
                            <div>
                                <h2 className="adm-section-title">User Management</h2>
                                <p className="adm-section-subtitle">Control who has access to AuditHippo and what they can do</p>
                            </div>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={openInvite}>
                                <span className="me-1">+</span> Invite User
                            </button>
                        </div>

                        {/* KPI Cards */}
                        <div className="adm-kpi-grid">
                            {KPIS.map(kpi => (
                                <div key={kpi.label} className="adm-user-kpi-card">
                                    <div className={`adm-user-kpi-icon adm-user-kpi-icon-${kpi.color}`}>{kpi.icon}</div>
                                    <div>
                                        <p className="adm-user-kpi-label">{kpi.label}</p>
                                        <p className="adm-user-kpi-value">{kpi.value}</p>
                                        <p className="adm-user-kpi-sub">{kpi.sub}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Filters */}
                        <div className="adm-filter-buttons">
                            {FILTERS.map(f => (
                                <button
                                    key={f}
                                    type="button"
                                    className={`adm-filter-btn${activeFilter === f ? ' active' : ''}`}
                                    onClick={() => setActiveFilter(f)}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* Table Header */}
                        <div className="adm-user-table-header">
                            <span className="adm-user-table-hcell">User</span>
                            <span className="adm-user-table-hcell">Role</span>
                            <span className="adm-user-table-hcell">Status</span>
                            <span className="adm-user-table-hcell">Instances</span>
                            <span className="adm-user-table-hcell">Last Active</span>
                            <span className="adm-user-table-hcell">&nbsp;</span>
                        </div>

                        {/* User List */}
                        <div className="d-flex flex-column gap-2">
                            {filtered.map(u => (
                                <div key={u.id} className="adm-user-row">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className={`adm-user-avatar adm-avatar-${u.role.toLowerCase()}`}>{u.initials}</div>
                                        <div>
                                            <p className="adm-user-name">{u.name}</p>
                                            <p className="adm-user-email">{u.email}</p>
                                        </div>
                                    </div>
                                    <div><span className={`adm-user-role-badge adm-role-${u.role.toLowerCase()}`}>{u.role}</span></div>
                                    <div>
                                        <span className={`adm-user-status-badge adm-status-${u.status.toLowerCase()}`}>
                                            <span className={`adm-status-dot adm-status-dot-${u.status.toLowerCase()}`} />
                                            {u.status}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1">
                                        {u.instances.length > 0
                                            ? u.instances.map(inst => <span key={inst.id || inst.name} className="adm-instance-chip">{inst.name}</span>)
                                            : <span className="adm-empty-cell">&mdash;</span>
                                        }
                                    </div>
                                    <div className="adm-user-last-active">{u.lastActive}</div>
                                    <div className="adm-user-actions">
                                        <button className="adm-user-edit-btn" type="button" onClick={() => openEdit(u)} disabled={removingId === u.id}>Edit</button>
                                        <button className="adm-user-remove-btn" type="button" onClick={() => handleRemove(u)} disabled={removingId === u.id}>
                                            {removingId === u.id ? 'Removing…' : 'Remove'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filtered.length === 0 && (
                            <div className="adm-user-empty">No users found for this filter.</div>
                        )}
                    </main>
                </div>
            </div>

            {/* Offcanvas: Invite User */}
            {inviteOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closeInvite} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Invite User</h2>
                                    <p className="panel-subtitle">Send an invitation to add a new user to AuditHippo</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closeInvite} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            <div className="d-flex flex-column gap-3">
                                {inviteSuccess && (
                                    <div className="adm-success-box">
                                        <div className="adm-success-check">&#10003;</div>
                                        <p className="adm-success-title">Invitation Sent</p>
                                        <p className="adm-success-sub">{inviteSuccess}</p>
                                    </div>
                                )}
                                {inviteError && (
                                    <div style={{
                                        background: '#FEF2F2',
                                        border: '1px solid #FECACA',
                                        borderRadius: 8,
                                        padding: '12px 14px',
                                        color: '#991B1B',
                                    }}>
                                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Failed to invite</p>
                                        <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.5 }}>{inviteError}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="adm-input-label">Name</label>
                                    <input
                                        className="adm-text-input"
                                        type="text"
                                        placeholder="e.g. Priya Sharma"
                                        style={showInviteValidation && !inviteForm.name.trim() ? { borderColor: '#EF4444' } : undefined}
                                        value={inviteForm.name}
                                        onChange={e => updateInvite('name', e.target.value)}
                                    />
                                    {showInviteValidation && !inviteForm.name.trim()
                                        ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Name is required.</p>
                                        : <p className="adm-input-hint mt-1">Full name of the person you want to invite.</p>}
                                </div>
                                <div>
                                    <label className="adm-input-label">Email</label>
                                    <input
                                        className="adm-text-input"
                                        type="email"
                                        placeholder="e.g. priya@acme.com"
                                        style={showInviteValidation && !inviteForm.email.trim() ? { borderColor: '#EF4444' } : undefined}
                                        value={inviteForm.email}
                                        onChange={e => updateInvite('email', e.target.value)}
                                    />
                                    {showInviteValidation && !inviteForm.email.trim()
                                        ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Email is required.</p>
                                        : <p className="adm-input-hint mt-1">The invitation will be sent to this email address.</p>}
                                </div>
                                <div>
                                    <label className="adm-input-label">Role</label>
                                    <select
                                        className="adm-text-input"
                                        value={inviteForm.role}
                                        onChange={e => updateInvite('role', e.target.value)}
                                    >
                                        {ROLE_OPTIONS.map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                    <p className="adm-input-hint mt-1">Controls what this user can see and do in AuditHippo.</p>
                                </div>
                                <div>
                                    <label className="adm-input-label">MC Connection</label>
                                    {connections.length === 0 ? (
                                        <p className="adm-input-hint" style={{ margin: 0 }}>No MC connections available.</p>
                                    ) : (
                                        <select
                                            className="adm-text-input"
                                            style={showInviteValidation && !inviteForm.connectionId ? { borderColor: '#EF4444' } : undefined}
                                            value={inviteForm.connectionId}
                                            onChange={e => updateInvite('connectionId', e.target.value)}
                                        >
                                            <option value="">Select MC connection</option>
                                            {connections.map(c => {
                                                const id   = c.id || c.connectionId;
                                                const name = c.name || c.instanceName || `${c.orgName || 'Instance'} — ${c.environment || ''}`;
                                                return <option key={id} value={id}>{name}</option>;
                                            })}
                                        </select>
                                    )}
                                    {showInviteValidation && !inviteForm.connectionId
                                        ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>MC Connection is required.</p>
                                        : <p className="adm-input-hint mt-1">Select the Marketing Cloud instance this user can access.</p>}
                                </div>
                            </div>
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={closeInvite} disabled={inviteSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={submitInvite} disabled={inviteSubmitting}>
                                {inviteSubmitting ? 'Sending…' : 'Send Invite'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Offcanvas: Edit User */}
            {editOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closeEdit} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Edit User</h2>
                                    <p className="panel-subtitle">Update this user's name, email, or role</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closeEdit} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <label className="adm-input-label">Name</label>
                                    <input
                                        className="adm-text-input"
                                        type="text"
                                        value={editForm.name}
                                        onChange={e => updateEdit('name', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="adm-input-label">Email</label>
                                    <input
                                        className="adm-text-input"
                                        type="email"
                                        value={editForm.email}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label className="adm-input-label">Role</label>
                                    <select
                                        className="adm-text-input"
                                        value={editForm.role}
                                        onChange={e => updateEdit('role', e.target.value)}
                                    >
                                        {ROLE_OPTIONS.map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="adm-input-label">MC Connection</label>
                                    {connections.length === 0 ? (
                                        <p className="adm-input-hint" style={{ margin: 0 }}>No MC connections available.</p>
                                    ) : (
                                        <select
                                            className="adm-text-input"
                                            value={editForm.connectionId}
                                            onChange={e => updateEdit('connectionId', e.target.value)}
                                        >
                                            <option value="">Select MC connection</option>
                                            {connections.map(c => {
                                                const id   = c.id || c.connectionId;
                                                const name = c.name || c.instanceName || `${c.orgName || 'Instance'} — ${c.environment || ''}`;
                                                return <option key={id} value={id}>{name}</option>;
                                            })}
                                        </select>
                                    )}
                                    <p className="adm-input-hint mt-1">Select the Marketing Cloud instance this user can access.</p>
                                </div>
                                {editNote && (
                                    <div className="adm-success-box">
                                        <div className="adm-success-check">&#10003;</div>
                                        <p className="adm-success-title">Connection Note</p>
                                        <p className="adm-success-sub">{editNote}</p>
                                    </div>
                                )}
                                {editError && (
                                    <p style={{ color: '#EF4444', margin: 0 }}>Failed to save: {editError}</p>
                                )}
                            </div>
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={closeEdit} disabled={editSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={submitEdit} disabled={editSubmitting || !editValid}>
                                {editSubmitting ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default UserManagement;
